/**
 * Pending seat-initialization persistence for grids whose batched
 * `initializeSeats` flow failed mid-sequence.
 *
 * When the launch wizard splits seat init into multiple BATCH_SIZE
 * chunks, any single batch can fail (gas estimate hiccup, wallet
 * rejection on the second/third confirmation, network drop). The grid
 * is live on-chain at that point — `createGrid` succeeded and at least
 * one batch of seats was written — but `seatsInitialized[gridId]` is
 * less than `totalSeats`, which the contract treats as "no seat can be
 * acquired" until the creator finishes the remaining batches.
 *
 * Strategy: stash the full seat-prices array (lower-cased
 * decimal-string form to keep the payload JSON-safe across page
 * reloads) keyed by composite gridId. The creator's tools panel reads
 * this on board mount and renders a "Resume initialization" CTA which
 * reads the on-chain `seatsInitialized` count, slices the array from
 * there, and re-submits the remaining batches via `executeActions`.
 *
 * NOTE: The prices are also recoverable from the `GridPricesInitialized`
 * event emitted by `createGrid`, but pulling the receipt back through
 * Envio adds an indexer-lag dependency we explicitly avoided in the
 * post-launch flow elsewhere. localStorage is the source of truth
 * the creator's own browser holds, so we use it directly.
 */

const STORAGE_KEY = 'takeover:pending-seat-init:v1';

export interface PendingSeatInitEntry {
  compositeGridId: string;
  managerAddress: string;
  numericId: string;
  /** Full seat-prices array, indexed by seatId (decimal strings of
   *  bigints — `seatPrices.map(p => p.toString())` from the wizard).
   *  Stored as strings rather than bigint because JSON.stringify
   *  doesn't natively serialize bigints. */
  seatPrices: string[];
  totalSeats: number;
  createdAt: number;
}

type PendingMap = Record<string, PendingSeatInitEntry>;

const MAX_AGE_MS = 7 * 24 * 60 * 60 * 1000;

function readMap(): PendingMap {
  if (typeof window === 'undefined') return {};
  try {
    const raw = window.localStorage.getItem(STORAGE_KEY);
    if (!raw) return {};
    const parsed = JSON.parse(raw);
    if (!parsed || typeof parsed !== 'object') return {};
    return parsed as PendingMap;
  } catch {
    return {};
  }
}

function writeMap(map: PendingMap): void {
  if (typeof window === 'undefined') return;
  try {
    if (Object.keys(map).length === 0) {
      window.localStorage.removeItem(STORAGE_KEY);
      return;
    }
    window.localStorage.setItem(STORAGE_KEY, JSON.stringify(map));
  } catch {
    // Quota / incognito profiles — degrade gracefully.
  }
}

export function setPendingSeatInit(entry: PendingSeatInitEntry): void {
  const map = readMap();
  map[entry.compositeGridId] = entry;
  writeMap(map);
}

export function getPendingSeatInit(
  compositeGridId: string,
): PendingSeatInitEntry | null {
  const map = readMap();
  const entry = map[compositeGridId];
  if (!entry) return null;
  if (Date.now() - entry.createdAt > MAX_AGE_MS) {
    delete map[compositeGridId];
    writeMap(map);
    return null;
  }
  return entry;
}

export function clearPendingSeatInit(compositeGridId: string): void {
  const map = readMap();
  if (!(compositeGridId in map)) return;
  delete map[compositeGridId];
  writeMap(map);
}
